import React, {
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState
} from 'react';

import {
  BrowserRouter,
  Navigate,
  Routes,
  Route,
  useLocation,
  useNavigate
} from 'react-router-dom';

import {
  QueryClient,
  QueryClientProvider
} from 'react-query';

import axios from 'axios';

import { ReactQueryDevtools } from 'react-query/devtools';
import { apiBase } from './constants/config';

import Admin from './components/admin';
import Dashboard from './components/consignment/Dashboard';
import Main from './components/Main';
import Registration from './components/Registration';
import ValidateAuth from './components/ValidateAuth';
import SuperAdminLogin from './components/SuperAdminLogin';
import Logout from './components/Logout';
import Invalid from './components/Invalid';
import UserRecord from './records/user';
import { useHydrateAuth } from './hooks/useAuth';
import entities, { entitiesState } from './reducers/entities';

export const AuthContext = React.createContext();
export const UserContext = React.createContext();
export const ApiContext = React.createContext();
export const EntitiesContext = React.createContext();

const queryClient = new QueryClient();

function App() {
  const [user, setUser] = useState(new UserRecord());
  const [auth, setAuth] = useState(null);
  const [state, dispatch] = useReducer(entities, entitiesState);
  // One client for the life of the app. Hooks capture this instance in their
  // query functions and RequireAuth sets the bearer header on it, so a query
  // that starts before auth hydrates (e.g. Main's useGetSale) retries on the
  // same instance and picks up the header. Creating it inline in JSX handed
  // every re-render a fresh, header-less client.
  const [api] = useState(() => axios.create({ baseURL: apiBase() }));

  return (
    <QueryClientProvider client={queryClient}>
      <AuthContext.Provider value={{ auth, setAuth }}>
        <ApiContext.Provider value={api}>
          <EntitiesContext.Provider value={{ state, dispatch }}>
            <UserContext.Provider value={{ user, setUser }}>
              <div className="App container">
                <BrowserRouter>
                  <Routes>
                    <Route exact path="/" element={<Main />} />
                    <Route path="/sa" element={<SuperAdminLogin />} />
                    <Route path="/register" element={<Registration />} />
                    <Route path="/auth/:token" element={<ValidateAuth />} />
                    <Route path="/logout" element={<Logout />} />
                    <Route path="/invalid" element={<Invalid />} />
                    <Route path="/dashboard/*"
                      element={
                        <RequireAuth>
                          <Dashboard />
                        </RequireAuth>
                      } />
                    <Route path="/admin/*"
                      element={
                        <RequireAuth>
                          <Admin />
                        </RequireAuth>
                      } />
                  </Routes>
                </BrowserRouter>
              </div>
            </UserContext.Provider>
          </EntitiesContext.Provider>
        </ApiContext.Provider>
      </AuthContext.Provider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

function RequireAuth({ children }) {
  const location = useLocation();
  const api = useContext(ApiContext);
  const user = useContext(UserContext);
  const result = useHydrateAuth();

  if (!result && user.user && !user.user.authenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (result && user.user.id) {
    // ProtectedSession must come before children: React runs passive effects
    // in tree order, so the bearer header and 403 interceptor are in place
    // before any child effect sends a request (react-query dispatches a
    // query's first request from its subscribing effect).
    return (
      <>
        <ProtectedSession api={api} token={result.data.data.id} />
        {children}
      </>
    );
  }
}

// Owns the client's authenticated state for the life of a protected route:
// sets the bearer header and a response interceptor that routes 403s to the
// login-error page, and removes both on unmount so a signed-out session's
// requests go out anonymous. RequireAuth used to set these during render,
// which added a duplicate interceptor on every re-render and, once the client
// became shared, would have left the header behind after logout.
function ProtectedSession({ api, token }) {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    const header = `Bearer ${token}`;
    let active = true;

    api.defaults.headers.common['Authorization'] = header;
    const id = api.interceptors.response.use(function (response) {
      return response;
    }, function (error) {
      // A request that started before this session ended must not redirect.
      if (active && error.response?.status === 403) {
        navigateRef.current('/invalid');
      }
      return Promise.reject(error);
    });

    return () => {
      active = false;
      api.interceptors.response.eject(id);
      // Only drop the header if it is still ours: when one protected route
      // replaces another, the new session's effect may have already run.
      if (api.defaults.headers.common['Authorization'] === header) {
        delete api.defaults.headers.common['Authorization'];
      }
    };
  }, [api, token]);

  return null;
}

export default App;
